import { zodResolver } from '@hookform/resolvers/zod'
import { isFuture } from 'date-fns'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import DatePicker from '@/components/DatePicker/DatePicker'
import FormField from '@/components/FormField'
import ModalForm from '@/components/Modal/ModalForm'
import RadioGroup from '@/components/Radio/RadioGroup'
import { formSchema, type FormSchema } from '@/constants/archive-payload'
import { toast } from '@/hooks/useToast'

import styles from './ArchiveStudentModal.module.scss'
import useScheduleArchiveStudents from '../hooks/useScheduleArchiveStudents'
import useBulkArchiveStudents from '../mutations/useBulkArchiveStudents'

type ArchiveStudentModalProps = {
  studentName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  ids: string[]
  onArchive: () => void
}

const ArchiveStudentModal = (props: ArchiveStudentModalProps) => {
  const { t } = useTranslation('students')

  const count = props.ids.length

  const handleOpenChange = (open: boolean) => {
    props.onOpenChange(open)
    if (!open) form.reset()
  }

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    defaultValues: {
      archiveMode: 'now',
      archiveDate: undefined
    }
  })

  const isDateValid = (date: Date) => isFuture(date)

  const archiveMode = form.watch('archiveMode')
  const isArchiveScheduled = archiveMode === 'scheduled'

  const shouldDateBeDisabled = (date: Date) => !isDateValid(date)

  const radioOptions = [
    {
      label: t('text.archive-now'),
      value: 'now'
    },
    { label: t('text.schedule-archiving'), value: 'scheduled' }
  ]

  const handleClose = () => {
    props.onOpenChange(false)
    form.reset()
  }

  const handleSuccesArchive = () => {
    props.onArchive()
    handleClose()
  }

  const {
    mutate: bulkArchiveStudents,
    isPending: isBulkArchiveStudentsLoading
  } = useBulkArchiveStudents({
    onSuccess: ({ successfully_archived, unsuccessfully_archived }) => {
      if (successfully_archived > 0) {
        toast({
          variant: 'success',
          title: props.studentName
            ? t('toast.student-archived', {
                NAME: props.studentName
              })
            : t('toast.students-archived', {
                count: successfully_archived
              })
        })
      }
      if (unsuccessfully_archived > 0) {
        toast({
          variant: 'error',
          title: props.studentName
            ? t('toast.student-archived-failed', {
                NAME: props.studentName
              })
            : t('toast.students-archived-failed', {
                count: unsuccessfully_archived
              })
        })
      }
      handleSuccesArchive()
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.students-archived-failed', {
          count
        })
      })
    }
  })

  const {
    mutate: bulkScheduledArchiveStudents,
    isPending: isBulkScheduledArchiveStudentsLoading
  } = useScheduleArchiveStudents({
    onSuccess: () => {
      handleSuccesArchive()
    },
    studentName: props.studentName
  })

  const handleSubmit = () => {
    const archiveDate = form.getValues('archiveDate')

    if (archiveDate) {
      bulkScheduledArchiveStudents({
        ids: props.ids,
        archiveDate
      })
    } else {
      bulkArchiveStudents({
        ids: props.ids
      })
    }
  }

  return (
    <ModalForm
      title={t('header.archive-students', {
        count
      })}
      open={props.open}
      onOpenChange={handleOpenChange}
      loading={
        isBulkArchiveStudentsLoading || isBulkScheduledArchiveStudentsLoading
      }
      form={form}
      onSubmit={handleSubmit}
      submitButtonVariant="danger"
      submitText={t('button.archive')}
      onCancel={handleClose}
      onClose={handleClose}
    >
      <p className={styles.description}>
        {props.studentName
          ? t('help.want-to-archive-student', {
              NAME: props.studentName
            })
          : t('help.want-to-archive-students', {
              count
            })}
      </p>
      <p className={styles.description}>{t('help.archive-student-contact')}</p>

      <div className={styles.wrapper}>
        <FormField
          control={form.control}
          id="archive-mode"
          label={t('label.archive-mode')}
          hideLabel
          required
          name="archiveMode"
          render={({ inputProps }) => (
            <RadioGroup {...inputProps} options={radioOptions} />
          )}
        />

        {isArchiveScheduled ? (
          <FormField
            control={form.control}
            id="archive-date"
            label={t('label.scheduled-date')}
            hideLabel
            className={styles.datePickerWrapper}
            required={isArchiveScheduled}
            name="archiveDate"
            render={({ inputProps }) => (
              <DatePicker
                disabledDate={shouldDateBeDisabled}
                placeholder={t('placeholder.select-date')}
                {...inputProps}
              />
            )}
          />
        ) : null}
      </div>
    </ModalForm>
  )
}

export default ArchiveStudentModal
